// /settings → "Мэдэгдлийн загвар" tab — create/edit modal (A7 §2.7a item 5).
// Fields mirror the backend Sequelize model: templateKey, channel, language,
// subject, body, description, isActive.

import {
  Checkbox,
  Card,
  Code,
  Group,
  Select,
  Stack,
  Text,
  Textarea,
  TextInput,
  Title,
} from "@mantine/core"
import { useForm } from "@mantine/form"
import { useTranslation } from "react-i18next"
import { HelpIcon } from "@dehub/theme"
import type { INotificationTemplate, ICreateNotificationTemplateInput, NotificationChannel } from "../../apis/notification-template"
import { useSettingsMutations } from "./hooks"
import { SettingsModal } from "./settings-modal"

// TODO: replace with real event registry once backend exposes one.
// Each entry is a system event that triggers a notification. Operators
// pick from this list; they should NOT invent new keys (would silently
// fail to fire).
const TEMPLATE_KEY_OPTIONS = [
  { value: "ORDER_CONFIRM",             variables: ["customerName", "orderCode", "totalAmount"] },
  { value: "ORDER_CANCELLED",           variables: ["customerName", "orderCode", "reason"] },
  { value: "DELIVERY_ASSIGNED",         variables: ["customerName", "orderCode", "driverName", "plateNumber"] },
  { value: "DELIVERY_OUT_FOR_DELIVERY", variables: ["customerName", "orderCode", "eta"] },
  { value: "DELIVERY_DONE",             variables: ["customerName", "orderCode"] },
  { value: "DELIVERY_FAILED",           variables: ["customerName", "orderCode", "reason"] },
  { value: "PAYMENT_RECEIVED",          variables: ["customerName", "orderCode", "totalAmount"] },
  { value: "PAYMENT_REFUNDED",          variables: ["customerName", "orderCode", "refundAmount"] },
  { value: "COD_RECONCILED",            variables: ["driverName", "workDate", "totalAmount"] },
] as const

type TemplateKey = (typeof TEMPLATE_KEY_OPTIONS)[number]["value"]

type FormShape = {
  templateKey: string
  channel: NotificationChannel
  language: string
  subject: string
  body: string
  description: string
  isActive: boolean
}

type Props = {
  opened: boolean
  onClose: () => void
  initial?: INotificationTemplate | null
  onSaved?: () => void
}

function fromInitial(r?: INotificationTemplate | null): FormShape {
  return {
    templateKey: r?.templateKey ?? "",
    channel    : (r?.channel as NotificationChannel) ?? "SMS",
    language   : r?.language ?? "mn",
    subject    : r?.subject ?? "",
    body       : r?.body ?? "",
    description: r?.description ?? "",
    isActive   : r?.isActive ?? true,
  }
}

export function NotificationTemplateFormModal({ opened, onClose, initial, onSaved }: Props) {
  const { t } = useTranslation("settings")
  const mutations = useSettingsMutations()
  const isEdit = !!initial

  const form = useForm<FormShape>({
    initialValues: fromInitial(initial),
    validate: {
      templateKey: (v) => (!v.trim() ? t("notification.validation.templateKeyRequired") : null),
      channel    : (v) => (!v ? t("notification.validation.channelRequired") : null),
      language   : (v) => (!v.trim() ? t("notification.validation.languageRequired") : null),
      body       : (v) => (!v.trim() ? t("notification.validation.bodyRequired") : null),
    },
  })

  const templateKeyData = TEMPLATE_KEY_OPTIONS.map((opt) => ({
    value: opt.value,
    label: t(`notification.templateKeys.${opt.value}`),
  }))

  const languageData = [
    { value: "mn", label: t("notification.languageOptions.mn") },
    { value: "en", label: t("notification.languageOptions.en") },
    { value: "ko", label: t("notification.languageOptions.ko") },
    { value: "zh", label: t("notification.languageOptions.zh") },
  ]

  const selectedKey = form.values.templateKey as TemplateKey | undefined
  const availableVars = TEMPLATE_KEY_OPTIONS.find((o) => o.value === selectedKey)?.variables ?? []

  const handleSubmit = async (values: FormShape) => {
    const payload: ICreateNotificationTemplateInput = {
      templateKey: values.templateKey.trim(),
      channel    : values.channel,
      language   : values.language.trim(),
      subject    : values.subject.trim() || null,
      body       : values.body.trim(),
      description: values.description.trim() || null,
      isActive   : values.isActive,
    }

    if (isEdit && initial) {
      await mutations.notifyTemplate.update(initial.id, payload)
    } else {
      await mutations.notifyTemplate.create(payload)
    }

    onSaved?.()
    onClose()
    form.reset()
  }

  return (
    <SettingsModal
      opened={opened}
      onClose={onClose}
      title={isEdit ? t("notification.modal.editTitle") : t("notification.modal.createTitle")}
      onSubmit={form.onSubmit(handleSubmit)}
      submitLabel={isEdit ? t("common.save") : t("common.create")}
      submitting={mutations.submitting}
    >

          {/* Card 1 — Identity */}
          <Card withBorder radius="md" p="md">
            <Title order={6} mb="sm">{t("notification.cards.identity.title")}</Title>
            <Stack gap="sm">
              <Select
                label={<>{t("notification.fields.templateKey.label")}<HelpIcon helpKey="order.settings.field.template-key" /></>}
                description={t("notification.fields.templateKey.description")}
                required
                disabled={isEdit}
                data={templateKeyData}
                value={form.values.templateKey || null}
                onChange={(v) => v && form.setFieldValue("templateKey", v)}
                error={form.errors.templateKey}
              />

              <Group grow>
                <Select
                  label={t("notification.fields.channel.label")}
                  required
                  data={[
                    { value: "SMS",   label: t("notification.options.channel.SMS") },
                    { value: "EMAIL", label: t("notification.options.channel.EMAIL") },
                    { value: "PUSH",  label: t("notification.options.channel.PUSH") },
                  ]}
                  {...form.getInputProps("channel")}
                />
                <Select
                  label={t("notification.fields.language.label")}
                  required
                  data={languageData}
                  value={form.values.language}
                  onChange={(v) => v && form.setFieldValue("language", v)}
                  error={form.errors.language}
                />
              </Group>
            </Stack>
          </Card>

          {/* Card 2 — Content */}
          <Card withBorder radius="md" p="md">
            <Title order={6} mb="sm">{t("notification.cards.content.title")}</Title>
            <Stack gap="sm">
              <TextInput
                label={t("notification.fields.subject.label")}
                {...form.getInputProps("subject")}
              />

              <Textarea
                label={<>{t("notification.fields.body.label")}<HelpIcon helpKey="order.settings.field.template-body" /></>}
                description={t("notification.fields.body.description")}
                required
                autosize
                minRows={8}
                maxRows={16}
                placeholder={t("notification.fields.body.placeholder", { customerName: "{{customerName}}", orderCode: "{{orderCode}}" })}
                {...form.getInputProps("body")}
              />

              {/* Variables hint Card */}
              <Card withBorder p="sm" radius="md" bg="gray.0">
                <Text size="sm" fw={500} mb="xs">
                  {t("notification.variables.title")}
                </Text>
                {selectedKey && availableVars.length > 0 ? (
                  <Group gap="xs">
                    {availableVars.map((v) => (
                      <Code key={v}>{`{{${v}}}`}</Code>
                    ))}
                  </Group>
                ) : (
                  <Text size="sm" c="dimmed">{t("notification.variables.empty")}</Text>
                )}
              </Card>

              <Textarea
                label={t("notification.fields.description.label")}
                autosize
                minRows={2}
                maxRows={4}
                {...form.getInputProps("description")}
              />
            </Stack>
          </Card>

          <Checkbox
            label={t("notification.fields.isActive.label")}
            checked={form.values.isActive}
            onChange={(e) => form.setFieldValue("isActive", e.currentTarget.checked)}
          />
    </SettingsModal>
  )
}
